import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter as FilterIcon, X } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import {
  ROOM_SETS,
  ROOMS,
  STYLE_FILTERS,
  AUDIENCE_FILTERS,
  PRICE_BUCKETS,
  SORT_OPTIONS,
  formatEur,
  sortRoomSets,
} from "../data/shop";

const inAudience = (set, key) => {
  if (key === "all") return true;
  if (key === "beide") return set.audience === "beide";
  if (key === "privat") return set.audience === "privat" || set.audience === "beide";
  if (key === "gewerblich") return set.audience === "gewerblich" || set.audience === "beide";
  return true;
};

const inPriceBucket = (set, key) => {
  if (key === "all") return true;
  const b = PRICE_BUCKETS.find((p) => p.key === key);
  if (!b) return true;
  return set.priceFrom >= b.min && set.priceFrom <= b.max;
};

export default function Shop() {
  useDocumentTitle(
    "Shop — Standard-Raumsets",
    "Kuratierte Standard-Raumsets von Livinora — vollständig komponierte Möbel-, Textil- und Lichtpakete für Ferienobjekte. Auswahl auf Anfrage."
  );

  const [room, setRoom] = useState("all");
  const [style, setStyle] = useState("all");
  const [audience, setAudience] = useState("all");
  const [price, setPrice] = useState("all");
  const [sort, setSort] = useState("recommended");
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile only

  // URL → filter sync (deep links from Stilserien / Home / Nora)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const qStyle = searchParams.get("style");
    const qRoom = searchParams.get("room") || searchParams.get("category");
    const qAudience = searchParams.get("audience");
    if (qStyle && STYLE_FILTERS.some((s) => s.key === qStyle)) setStyle(qStyle);
    if (qRoom && ROOMS.some((r) => r.key === qRoom)) setRoom(qRoom);
    if (qAudience && AUDIENCE_FILTERS.some((a) => a.key === qAudience)) setAudience(qAudience);
    // run only on mount / when params change
  }, [searchParams]);

  const filtered = useMemo(() => {
    const list = ROOM_SETS.filter((s) => {
      if (room !== "all" && s.room !== room) return false;
      if (style !== "all" && s.style !== style) return false;
      if (!inAudience(s, audience)) return false;
      if (!inPriceBucket(s, price)) return false;
      return true;
    });
    return sortRoomSets(list, sort);
  }, [room, style, audience, price, sort]);

  const activeCount =
    (room !== "all" ? 1 : 0) +
    (style !== "all" ? 1 : 0) +
    (audience !== "all" ? 1 : 0) +
    (price !== "all" ? 1 : 0) +
    (sort !== "recommended" ? 1 : 0);

  const resetAll = () => {
    setRoom("all");
    setStyle("all");
    setAudience("all");
    setPrice("all");
    setSort("recommended");
  };

  // Lock body scroll while mobile filter drawer is open
  useEffect(() => {
    if (!filtersOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setFiltersOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [filtersOpen]);

  // Active filter chips outside the drawer (mobile)
  const activeChips = [
    room !== "all" && {
      label: ROOMS.find((r) => r.key === room)?.label,
      onRemove: () => setRoom("all"),
      testId: "active-chip-room",
    },
    style !== "all" && {
      label: STYLE_FILTERS.find((s) => s.key === style)?.label,
      onRemove: () => setStyle("all"),
      testId: "active-chip-style",
    },
    audience !== "all" && {
      label: AUDIENCE_FILTERS.find((a) => a.key === audience)?.label,
      onRemove: () => setAudience("all"),
      testId: "active-chip-audience",
    },
    price !== "all" && {
      label: PRICE_BUCKETS.find((p) => p.key === price)?.label,
      onRemove: () => setPrice("all"),
      testId: "active-chip-price",
    },
    sort !== "recommended" && {
      label: SORT_OPTIONS.find((o) => o.key === sort)?.label,
      onRemove: () => setSort("recommended"),
      testId: "active-chip-sort",
    },
  ].filter(Boolean);

  return (
    <div data-testid="page-shop">
      {/* Editorial header banner */}
      <section
        className="relative pt-20 sm:pt-24 md:pt-28"
        data-testid="shop-header-banner"
        aria-label="Shop Hero"
      >
        <div className="relative w-full h-[42vh] sm:h-[48vh] md:h-[56vh] lg:h-[60vh] min-h-[320px] max-h-[680px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=80"
            alt="Kuratiertes Livinora-Interieur"
            className="w-full h-full object-cover"
            data-testid="shop-header-image"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(246,245,242,0.10) 0%, rgba(246,245,242,0.0) 35%, rgba(26,26,26,0.55) 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 pb-8 sm:pb-10 md:pb-14">
            <div className="container-livinora">
              <span className="section-label section-label--inverse">
                <span className="section-label-num">Shop</span>
                <span className="section-label-rule" aria-hidden="true" />
                <span className="section-label-text">Kuratierte Raumsets</span>
              </span>
              <h1
                className="display-xl mt-5 sm:mt-7 text-white text-[36px] sm:text-[60px] md:text-[76px] lg:text-[88px] text-balance max-w-5xl"
                data-testid="shop-hero-h1"
              >
                Vorkonfigurierte Raumsets,
                <br className="hidden sm:block" /> anfragbar.
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="pt-12 sm:pt-16 md:pt-20 pb-12 sm:pb-16 md:pb-20">
        <div className="container-livinora">
          <Reveal>
            <p className="prose-livinora prose-livinora--lead max-w-2xl">
              Kein Möbelhandel — sondern fertig durchdachte Raumkonzepte. Jedes Set ist eine
              vollständige Komposition aus Möbeln, Textilien und Licht in einer Stilserie. Mit
              Richtpreis zur Orientierung, Inhalt klar definiert, Anfrage statt Checkout.
            </p>
          </Reveal>

          {/* Shop vs Configurator */}
          <div
            className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
            data-testid="shop-vs-konfigurator"
          >
            <Reveal>
              <div className="card-depth p-6 sm:p-8 h-full flex flex-col">
                <span className="micro-label">Shop</span>
                <h3 className="serif text-2xl mt-4 leading-snug">Standardisierte Raumsets.</h3>
                <p className="mt-4 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
                  Schnelle Auswahl. Ideal für Ergänzungen, Nachbestellungen und kleinere
                  Standardbedarfe — mit Richtpreis und definiertem Inhalt.
                </p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="card-depth card-depth--warm p-6 sm:p-8 h-full flex flex-col">
                <span className="micro-label">Konfigurator</span>
                <h3 className="serif text-2xl mt-4 leading-snug">Komplette Einrichtungskonzepte.</h3>
                <p className="mt-4 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
                  Geführte Zusammenstellung. Ideal für umfassendere Projekte — vom Objekttyp über
                  Stilserie bis zum Gesamtkonzept.
                </p>
                <Link
                  to="/konfigurator"
                  className="btn-line mt-6 self-start"
                  data-testid="shop-link-konfigurator"
                >
                  Zum Konfigurator
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="border-t border-[color:var(--livinora-border)]">
        <div className="container-livinora py-7 sm:py-9 lg:py-10">
          {/* Mobile filter trigger + active chips */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] px-5 py-3.5 border border-[color:var(--livinora-ink)] bg-transparent active:bg-[color:var(--livinora-ink)] active:text-[color:var(--livinora-paper)] transition-colors min-h-[48px]"
                data-testid="shop-filters-toggle"
                aria-expanded={filtersOpen}
                aria-controls="shop-filter-drawer"
              >
                <FilterIcon size={14} />
                <span>Filter</span>
                {activeCount > 0 && (
                  <span className="text-[color:var(--livinora-paper)] bg-[color:var(--livinora-ink)] inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full ml-1">
                    {activeCount}
                  </span>
                )}
              </button>
              <span
                className="text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-muted)] whitespace-nowrap"
                data-testid="shop-result-count"
              >
                {filtered.length} {filtered.length === 1 ? "Set" : "Sets"}
              </span>
            </div>

            {/* Active chips row (horizontal scroll) */}
            {activeChips.length > 0 && (
              <div
                className="mt-4 -mx-5 px-5 flex gap-2 overflow-x-auto pb-1 scroll-smooth"
                style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
                data-testid="shop-active-chips"
              >
                {activeChips.map((chip) => (
                  <button
                    key={chip.testId}
                    type="button"
                    onClick={chip.onRemove}
                    data-testid={chip.testId}
                    className="shrink-0 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-[color:var(--livinora-ink)] bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] min-h-[36px]"
                  >
                    <span>{chip.label}</span>
                    <X size={12} strokeWidth={2.2} />
                  </button>
                ))}
                {activeChips.length > 1 && (
                  <button
                    type="button"
                    onClick={resetAll}
                    data-testid="shop-active-chips-reset"
                    className="shrink-0 text-[11px] uppercase tracking-[0.18em] px-3 py-2 underline underline-offset-4 text-[color:var(--livinora-ink)] min-h-[36px]"
                  >
                    Alle entfernen
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Desktop filter grid */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-3 gap-x-10 gap-y-8">
              <FilterGroup label="Raum" testId="shop-filter-room">
                <FilterChip active={room === "all"} onClick={() => setRoom("all")} testId="filter-room-all">
                  Alle Räume
                </FilterChip>
                {ROOMS.map((r) => (
                  <FilterChip key={r.key} active={room === r.key} onClick={() => setRoom(r.key)} testId={`filter-room-${r.key}`}>
                    {r.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Stilserie" testId="shop-filter-style">
                <FilterChip active={style === "all"} onClick={() => setStyle("all")} testId="filter-style-all">
                  Alle Stile
                </FilterChip>
                {STYLE_FILTERS.map((s) => (
                  <FilterChip key={s.key} active={style === s.key} onClick={() => setStyle(s.key)} testId={`filter-style-${s.key}`}>
                    {s.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Geeignet für" testId="shop-filter-audience">
                <FilterChip active={audience === "all"} onClick={() => setAudience("all")} testId="filter-audience-all">
                  Alle
                </FilterChip>
                {AUDIENCE_FILTERS.map((a) => (
                  <FilterChip key={a.key} active={audience === a.key} onClick={() => setAudience(a.key)} testId={`filter-audience-${a.key}`}>
                    {a.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Preisrahmen" testId="shop-filter-price">
                <FilterChip active={price === "all"} onClick={() => setPrice("all")} testId="filter-price-all">
                  Alle Preise
                </FilterChip>
                {PRICE_BUCKETS.map((p) => (
                  <FilterChip key={p.key} active={price === p.key} onClick={() => setPrice(p.key)} testId={`filter-price-${p.key}`}>
                    {p.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Sortierung" testId="shop-filter-sort">
                {SORT_OPTIONS.map((o) => (
                  <FilterChip key={o.key} active={sort === o.key} onClick={() => setSort(o.key)} testId={`filter-sort-${o.key}`}>
                    {o.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <div className="flex flex-col justify-end">
                {activeCount > 0 ? (
                  <button
                    type="button"
                    onClick={resetAll}
                    className="text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-ink)] underline-offset-4 hover:underline self-start"
                    data-testid="shop-filters-reset"
                  >
                    Alle Filter zurücksetzen
                  </button>
                ) : (
                  <span className="text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-muted)] self-start">
                    Keine Filter aktiv
                  </span>
                )}
              </div>
            </div>
            <div
              className="mt-6 text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-muted)]"
              data-testid="shop-result-count-desktop"
            >
              {filtered.length} {filtered.length === 1 ? "Set" : "Sets"}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile filter drawer (slide-up sheet) */}
      <div
        id="shop-filter-drawer"
        className={`lg:hidden fixed inset-0 z-[60] ${filtersOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!filtersOpen}
        data-testid="shop-filter-drawer"
      >
        {/* Backdrop */}
        <button
          type="button"
          aria-label="Filter schließen"
          onClick={() => setFiltersOpen(false)}
          className={`absolute inset-0 bg-[color:var(--livinora-ink)]/45 transition-opacity duration-500 ${
            filtersOpen ? "opacity-100" : "opacity-0"
          }`}
          tabIndex={filtersOpen ? 0 : -1}
        />
        {/* Sheet */}
        <div
          className={`absolute inset-x-0 bottom-0 bg-[color:var(--livinora-paper)] border-t border-[color:var(--livinora-border)] shadow-[0_-24px_60px_-30px_rgba(0,0,0,0.45)] transform transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            filtersOpen ? "translate-y-0" : "translate-y-full"
          }`}
          style={{ maxHeight: "88vh" }}
          role="dialog"
          aria-modal="true"
          aria-label="Filter & Sortierung"
        >
          {/* Handle */}
          <div className="pt-3 pb-1 flex justify-center">
            <span aria-hidden className="block w-12 h-1 bg-[color:var(--livinora-border)] rounded-full" />
          </div>
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-6 pt-2 pb-4 border-b border-[color:var(--livinora-border)]">
            <span className="overline">Filter &amp; Sortierung</span>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              data-testid="shop-filter-drawer-close"
              aria-label="Schließen"
              className="w-10 h-10 -mr-2 inline-flex items-center justify-center text-[color:var(--livinora-ink)]"
            >
              <X size={18} />
            </button>
          </div>
          {/* Body */}
          <div className="px-5 sm:px-6 py-6 overflow-y-auto" style={{ maxHeight: "calc(88vh - 168px)" }}>
            <div className="flex flex-col gap-7">
              <FilterGroup label="Raum" testId="shop-filter-room-mobile">
                <FilterChip active={room === "all"} onClick={() => setRoom("all")} testId="filter-room-all-m">
                  Alle Räume
                </FilterChip>
                {ROOMS.map((r) => (
                  <FilterChip key={r.key} active={room === r.key} onClick={() => setRoom(r.key)} testId={`filter-room-${r.key}-m`}>
                    {r.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Stilserie" testId="shop-filter-style-mobile">
                <FilterChip active={style === "all"} onClick={() => setStyle("all")} testId="filter-style-all-m">
                  Alle Stile
                </FilterChip>
                {STYLE_FILTERS.map((s) => (
                  <FilterChip key={s.key} active={style === s.key} onClick={() => setStyle(s.key)} testId={`filter-style-${s.key}-m`}>
                    {s.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Geeignet für" testId="shop-filter-audience-mobile">
                <FilterChip active={audience === "all"} onClick={() => setAudience("all")} testId="filter-audience-all-m">
                  Alle
                </FilterChip>
                {AUDIENCE_FILTERS.map((a) => (
                  <FilterChip key={a.key} active={audience === a.key} onClick={() => setAudience(a.key)} testId={`filter-audience-${a.key}-m`}>
                    {a.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Preisrahmen" testId="shop-filter-price-mobile">
                <FilterChip active={price === "all"} onClick={() => setPrice("all")} testId="filter-price-all-m">
                  Alle Preise
                </FilterChip>
                {PRICE_BUCKETS.map((p) => (
                  <FilterChip key={p.key} active={price === p.key} onClick={() => setPrice(p.key)} testId={`filter-price-${p.key}-m`}>
                    {p.label}
                  </FilterChip>
                ))}
              </FilterGroup>
              <FilterGroup label="Sortierung" testId="shop-filter-sort-mobile">
                {SORT_OPTIONS.map((o) => (
                  <FilterChip key={o.key} active={sort === o.key} onClick={() => setSort(o.key)} testId={`filter-sort-${o.key}-m`}>
                    {o.label}
                  </FilterChip>
                ))}
              </FilterGroup>
            </div>
          </div>
          {/* Footer */}
          <div
            className="flex items-stretch gap-3 border-t border-[color:var(--livinora-border)] bg-[color:var(--livinora-paper)] px-5 sm:px-6 py-4"
            style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
          >
            <button
              type="button"
              onClick={resetAll}
              data-testid="shop-filters-reset-mobile"
              className="flex-1 text-xs uppercase tracking-[0.18em] border border-[color:var(--livinora-ink)] text-[color:var(--livinora-ink)] active:bg-[color:var(--livinora-ink)] active:text-[color:var(--livinora-paper)] py-3.5 min-h-[52px] transition-colors"
            >
              Alle zurücksetzen
            </button>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              data-testid="shop-filters-apply"
              className="flex-[1.4] btn-livinora btn-primary justify-center min-h-[52px]"
            >
              Filter anwenden ({filtered.length})
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="pb-24 sm:pb-28 md:pb-32 border-t border-[color:var(--livinora-border)]">
        <div className="container-livinora pt-12 sm:pt-16">
          {filtered.length === 0 ? (
            <div className="hairline border bg-[color:var(--livinora-paper)] p-10 text-center" data-testid="shop-empty">
              <span className="overline">Keine Treffer</span>
              <p className="mt-4 serif text-2xl">Keine Sets in dieser Kombination.</p>
              <p className="mt-3 text-sm text-[color:var(--livinora-muted)]">
                Bitte einen Filter zurücksetzen — oder direkt eine individuelle Anfrage stellen.
              </p>
              <div className="mt-6 flex flex-wrap gap-4 justify-center">
                <button onClick={resetAll} className="btn-livinora btn-line">Filter zurücksetzen</button>
                <Link to="/kontakt" className="btn-livinora btn-primary">
                  Individuelle Anfrage <span>→</span>
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 sm:gap-y-16">
              {filtered.map((s, i) => (
                <Reveal key={s.slug} delay={i * 60}>
                  <Link
                    to={`/shop/${s.slug}`}
                    className="group block"
                    data-testid={`shop-card-${s.slug}`}
                  >
                    <div className="media-wrap aspect-[4/5]">
                      <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="mt-5 flex items-baseline justify-between gap-4">
                      <span className="micro-label">{s.style_label}</span>
                      <span className="text-[10.5px] uppercase tracking-[0.22em] text-[color:var(--livinora-stone-deep)]">
                        {s.components.length} Komponenten
                      </span>
                    </div>
                    <h3 className="serif mt-3 text-2xl sm:text-[28px] leading-[1.05] transition-transform duration-700 group-hover:translate-x-1">
                      {s.name}
                    </h3>
                    <p className="mt-3 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
                      {s.subtitle}
                    </p>

                    {/* Suited-for pills */}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {s.suitedFor.slice(0, 3).map((x) => (
                        <span
                          key={x}
                          className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-[color:var(--livinora-hairline)] text-[color:var(--livinora-ink-soft)]"
                        >
                          {x}
                        </span>
                      ))}
                    </div>

                    {/* Price + CTA */}
                    <div className="mt-5 pt-5 border-t border-[color:var(--livinora-hairline)] flex items-end justify-between gap-4">
                      <div>
                        <div className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">Richtpreis</div>
                        <div className="serif text-xl mt-1.5" data-testid={`shop-card-price-${s.slug}`}>
                          ab {formatEur(s.priceFrom)}
                        </div>
                      </div>
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--livinora-ink)] transition-transform duration-500 group-hover:translate-x-1">
                        Set ansehen →
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer note / CTA */}
      <section className="py-20 sm:py-24 md:py-28 bg-[color:var(--livinora-paper-warm)] border-t border-[color:var(--livinora-hairline)]">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel index="∞" text="Inquiry-first" />
            </Reveal>
            <Reveal delay={60}>
              <h2 className="display-xl mt-7 text-4xl sm:text-5xl md:text-6xl text-balance">
                Sie wählen das Set — wir liefern Konzept, Angebot und Umsetzung.
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="prose-livinora mt-7">
                Alle Preise sind Richtpreise zur Orientierung, zzgl. Lieferung und Montage.
                Mengen, Staffelpreise und projektspezifische Anpassungen klären wir persönlich.
              </p>
            </Reveal>
          </div>
          <Reveal delay={220} className="lg:col-span-5 lg:text-right">
            <Link to="/kontakt" className="btn-livinora btn-primary btn-magnetic" data-testid="shop-cta-individual">
              Individuelle Anfrage <span>→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function FilterGroup({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="overline mb-4">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={`text-xs uppercase tracking-[0.18em] px-4 py-2 border transition-colors duration-300 ${
        active
          ? "bg-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)] border-[color:var(--livinora-ink)]"
          : "bg-transparent text-[color:var(--livinora-ink)] border-[color:var(--livinora-border)] hover:border-[color:var(--livinora-ink)]"
      }`}
    >
      {children}
    </button>
  );
}
